'use client'

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { api, apiHeaders } from '../configs/api'
import { useAuth } from './AuthContext'

export type ProductDataType = {
    id: number
    name: string
    image?: string
    isDefault: boolean
    businessCount: number
}

export type ProductDetailType = {
    product: Omit<ProductDataType, 'businessCount'>
    business: {
        id: number
        company: string
        pipeline: string
        step: string
        color: string
        value: string | null
        dateConclude: string | null
    }[]
}

type ProductType = {
    loading: boolean
    products: ProductDataType[]
    setProducts: React.Dispatch<React.SetStateAction<ProductDataType[]>>
    filteredProducts: ProductDataType[]
    setFilteredProducts: React.Dispatch<React.SetStateAction<ProductDataType[]>>
    detail: ProductDetailType | null
    setDetail: React.Dispatch<React.SetStateAction<ProductDetailType | null>>
    loadProducts: () => Promise<void>
    getProduct: (id: string | number) => Promise<void>
    setProductDefault: (id: number, isDefault: boolean) => Promise<boolean>
}

const Product = createContext<ProductType | undefined>(undefined)

export const ProductProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { unity, token } = useAuth()
    const [loading, setLoading] = useState(false)
    const [products, setProducts] = useState<ProductDataType[]>([])
    const [filteredProducts, setFilteredProducts] = useState<ProductDataType[]>([])
    const [detail, setDetail] = useState<ProductDetailType | null>(null)

    const loadProducts = useCallback(async () => {
        if (!unity || !token) return

        setLoading(true)
        try {
            const response = await fetch(`${api}product/getData`, {
                method: 'POST',
                headers: apiHeaders(token),
                body: JSON.stringify({ unityID: unity.unityID })
            })
            const result = await response.json()
            if (!response.ok || !result.success) throw new Error(result.message)

            const list = (result.products || []).map((product: ProductDataType) => ({
                ...product,
                image: product.image || undefined
            })) as ProductDataType[]
            setProducts(list)
            setFilteredProducts(list)
        } catch (error) {
            setProducts([])
            setFilteredProducts([])
            toast.error(error instanceof Error && error.message ? error.message : 'Erro ao carregar produtos!')
        } finally {
            setLoading(false)
        }
    }, [token, unity])

    const getProduct = useCallback(
        async (id: string | number) => {
            if (!unity || !token) return

            try {
                const response = await fetch(`${api}product/getDetail`, {
                    method: 'POST',
                    headers: apiHeaders(token),
                    body: JSON.stringify({ id, unityID: unity.unityID })
                })
                const result = await response.json()
                if (!response.ok || !result.success) throw new Error(result.message)
                setDetail({
                    ...result.detail,
                    product: {
                        ...result.detail.product,
                        image: result.detail.product.image || undefined
                    }
                })
            } catch (error) {
                setDetail(null)
                toast.error(error instanceof Error && error.message ? error.message : 'Erro ao carregar produto!')
            }
        },
        [token, unity]
    )

    const setProductDefault = async (id: number, isDefault: boolean) => {
        if (!unity || !token) return false

        try {
            const response = await fetch(`${api}product/setDefault`, {
                method: 'POST',
                headers: apiHeaders(token),
                body: JSON.stringify({ id, isDefault, unityID: unity.unityID })
            })
            const result = await response.json()
            if (!response.ok || !result.success) throw new Error(result.message)

            await loadProducts()
            if (detail?.product.id === id) await getProduct(id)
            toast.success(result.message)
            return true
        } catch (error) {
            toast.error(error instanceof Error && error.message ? error.message : 'Erro ao alterar produto padrão!')
            return false
        }
    }

    useEffect(() => {
        loadProducts()
    }, [loadProducts])

    return (
        <Product.Provider
            value={{
                loading,
                products,
                setProducts,
                filteredProducts,
                setFilteredProducts,
                detail,
                setDetail,
                loadProducts,
                getProduct,
                setProductDefault
            }}
        >
            {children}
        </Product.Provider>
    )
}

export function useProduct() {
    const context = useContext(Product)
    if (context === undefined) throw new Error('useProduct must be used within a ProductProvider')
    return context
}
